import { Endpoint } from '@business/api/endpoint';
import { SupportTicketAPI } from '@business/api/SupportTicket';
import { UserId } from '@business/entities/account';
import { OrgId } from '@business/entities/organization';
import { SenderID } from '@business/entities/sender';
import {
  CompactSupportTicketResponse,
  SupportTicketActionName,
  SupportTicketRequest,
  SupportTicketResponse,
  TestTicketRequest,
  TicketFileId,
  TicketId,
  TicketSearchParams,
} from '@business/entities/SupportTicket';
import { TicketReplyId, TicketReplyRequest } from '@business/entities/SupportTicket/TicketReplyResponse';
import { TenantId } from '@business/entities/tenant';
import { handleMessage, requestMessage } from '@business/messages';
import { FlashMessage } from '@core/components/FlashMessage';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import axios from 'axios';
import { defer, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { SupportTicketPublicRequest, SupportTicketStatus, TicketOrgRequest } from './../../entities/SupportTicket/SupportTicketResponse';

export class SupportTicketBloc extends BaseBloc {
  private readonly ticketRepo = new Repository<SupportTicketResponse | CompactSupportTicketResponse>({
    getItemId: ticket => ticket.id,
  });

  readonly tickets$ = this.ticketRepo.items$;
  readonly selectTicket = this.ticketRepo.selectItem;

  onReset() {
    this.ticketRepo.reset();
  }

  testTicket = (data: TestTicketRequest) => {
    return SupportTicketAPI.testTicket(data).pipe(
      this.ticketRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_ticket'),
      }),
    );
  };

  fetchTickets = (data: TicketSearchParams) => {
    return SupportTicketAPI.fetchTickets(data);
  };

  fetchTicket = (id: TicketId) => {
    return SupportTicketAPI.fetchTicket(id).pipe(
      this.ticketRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_ticket_error') }),
    );
  };

  createTicket = (data: SupportTicketRequest, files: File[]) => {
    return SupportTicketAPI.createTicket(data).pipe(
      switchMap(item => this.uploadCreateTicketFiles(item.id, files, R.first(item.replies)!.id)),
      handleMessage({
        type: requestMessage('create_ticket'),
      }),
    );
  };

  createPublicTicket = (data: SupportTicketPublicRequest, files: File[]) => {
    return SupportTicketAPI.createPublicTicket(data, files).pipe(
      handleMessage({
        type: requestMessage('create_ticket'),
      }),
    );
  };

  updateTicket = (id: TicketId, data: SupportTicketRequest) => {
    return SupportTicketAPI.updateTicket(id, data).pipe(
      this.ticketRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_ticket'),
      }),
    );
  };

  deleteTicket = (id: TicketId) => {
    return showDeleteConfirmation('Delete Ticket', 'Do you really want to remove this ticket?').pipe(
      switchMap(() => SupportTicketAPI.deleteTicket(id)),
      this.ticketRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_ticket'),
      }),
    );
  };

  createTicketReply = (
    id: TicketId,
    data: TicketReplyRequest,
    files: File[] = [],
    onUndo?: (replyId: TicketReplyId) => void,
    storageKey?: string,
  ) => {
    return SupportTicketAPI.createTicketReply(id, data, files, storageKey).pipe(
      this.ticketRepo.ops.updateOne(item => ({
        id,
        changes: ticket => ({ replies: [...(ticket as SupportTicketResponse).replies, item], selectedEmails: item.emails }),
      })),
      //   tap(item => {
      //     files.length > 0 && this.uploadTicketFiles(id, files, item.id);
      //   }),
      //   handleMessage({
      //     type: requestMessage('create_ticket_reply'),
      //   }),
      tap(item => {
        FlashMessage.success('Reply sent successfully', '', {
          buttons: [FlashMessage.button('Undo', 'info', () => onUndo && onUndo(item.id))],
          autoHide: true,
          duration: 10000,
          progressBar: true,
          layout: 'bottomLeft',
          forceTimeout: true,
        });
      }),
    );
  };

  uploadCreateTicketFiles = (id: TicketId, files: File[], replyId: TicketReplyId = '0') => {
    if (!files.length) {
      return of(null);
    }
    const url = Endpoint.dumpUrl('@gc');
    const client = axios.create({
      baseURL: url,
      headers: Endpoint.dumpHeaders,
    });
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('file[' + i + ']', files[i]);
    }
    return defer(() => client.post(`/support-ticket/files/${id}/${replyId}`, formData).catch(() => {}));
  };

  uploadTicketFiles = (id: TicketId, files: File[], replyId: TicketReplyId = '0') => {
    if (!files.length) {
      return of(null);
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const object = this;
    const url = Endpoint.dumpUrl('@gc');
    const client = axios.create({
      baseURL: url,
      headers: Endpoint.dumpHeaders,
    });
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('file[' + i + ']', files[i]);
    }
    client
      .post(`/support-ticket/files/${id}/${replyId}`, formData)
      .then(() => {
        if (replyId !== '0') {
          object.syncTicketReply(id, replyId).subscribe();
        }
        object.fetchTicketFiles(id).subscribe();
      })
      .catch(() => {});
  };

  syncTicketReply = (id: TicketId, replyId: TicketReplyId) => {
    return SupportTicketAPI.fetchTicketReply(replyId).pipe(
      this.ticketRepo.ops.updateOne(item => ({
        id,
        changes: ticket => ({
          replies: (ticket as SupportTicketResponse).replies.map(reply => {
            return reply.id == item.id ? item : reply;
          }),
        }),
      })),
    );
  };

  deleteTicketFile = (ticketId: TicketId, fileId: TicketFileId) => {
    return showDeleteConfirmation('Delete File', 'Do you really want to remove this file?').pipe(
      switchMap(() => SupportTicketAPI.deleteTicketFile(fileId)),
      tap(() => this.fetchTicketFiles(ticketId).subscribe()),
      handleMessage({
        type: requestMessage('delete_channel'),
      }),
    );
  };

  createTicketNote = (
    id: TicketId,
    userId: UserId[],
    body: string,
    senderId: SenderID,
    files: File[] = [],
    onUndo?: (replyId: TicketReplyId) => void,
    storageKey?: string,
  ) => {
    return SupportTicketAPI.createTicketNote(id, userId, body, senderId, files, storageKey).pipe(
      this.ticketRepo.ops.updateOne(item => ({
        id,
        changes: ticket => ({ replies: [...(ticket as SupportTicketResponse).replies, item] }),
      })),
      //   handleMessage({
      //     type: requestMessage('create_ticket_note'),
      //   }),
      tap(item => {
        FlashMessage.success('Internal note sent successfully', '', {
          buttons: [FlashMessage.button('Undo', 'danger', () => onUndo && onUndo(item.id))],
          autoHide: true,
          duration: 10000,
          progressBar: true,
          layout: 'bottomLeft',
          forceTimeout: true,
        });
      }),
    );
  };

  createEscalateReply = (id: TicketId, body: string, files: File[] = [], onUndo?: (replyId: TicketReplyId) => void, storageKey?: string) => {
    return SupportTicketAPI.createEscalateReply(id, body, files, storageKey).pipe(
      this.ticketRepo.ops.updateOne(item => ({
        id,
        changes: ticket => ({ replies: [...(ticket as SupportTicketResponse).replies, item] }),
      })),
      tap(item => {
        FlashMessage.success('Reply sent successfully', '', {
          buttons: [FlashMessage.button('Undo', 'danger', () => onUndo && onUndo(item.id))],
          autoHide: true,
          duration: 10000,
          progressBar: true,
          layout: 'bottomLeft',
          forceTimeout: true,
        });
      }),
    );
  };

  undoReply = (id: TicketId, replyId: TicketReplyId) => {
    return SupportTicketAPI.undoReply(replyId).pipe(
      this.ticketRepo.ops.updateOne(() => ({
        id,
        changes: ticket => ({
          replies: (ticket as SupportTicketResponse).replies.filter(reply => {
            return reply.id !== replyId;
          }),
        }),
      })),
    );
  };

  updateTicketReply = (id: TicketId, replyId: TicketReplyId, body: string) => {
    return SupportTicketAPI.updateTicketReply(replyId, body).pipe(
      this.ticketRepo.ops.updateOne(item => ({
        id,
        changes: ticket => ({
          replies: (ticket as SupportTicketResponse).replies.map(reply => {
            return reply.id == item.id ? item : reply;
          }),
        }),
      })),
      handleMessage({
        type: requestMessage('update_ticket_reply'),
      }),
    );
  };

  splitTicket = (replyId: TicketReplyId) => {
    return showDeleteConfirmation('Split Reply to New Ticket', 'Do you really want to split the ticket from this reply?', {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => SupportTicketAPI.splitTicket(replyId)),
      this.ticketRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_ticket'),
      }),
    );
  };

  deleteTicketReply = (id: TicketId, replyId: TicketReplyId) => {
    return showDeleteConfirmation('Delete Reply', 'Do you really want to remove this reply?').pipe(
      switchMap(() => SupportTicketAPI.deleteTicketReply(replyId)),
      this.ticketRepo.ops.updateOne(() => ({
        id,
        changes: ticket => ({
          replies: (ticket as SupportTicketResponse).replies.filter(reply => {
            return reply.id !== replyId;
          }),
        }),
      })),
      handleMessage({
        type: requestMessage('delete_ticket_reply'),
      }),
    );
  };

  escalateTicket = (id: TicketId, tenantId: TenantId) => {
    return showDeleteConfirmation('Escalate Ticket', 'Are you sure you want to escalate ticket?', {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => SupportTicketAPI.escalateTicket(id, tenantId)),
      this.ticketRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_ticket'),
      }),
    );
  };

  removeEscalation = (id: TicketId) => {
    return showDeleteConfirmation('Unassigned ticket', 'Are you sure you want to unassigned ticket?', {
      btnLabel: 'Confirm',
    }).pipe(
      switchMap(() => SupportTicketAPI.removeEscalated(id)),
      this.ticketRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_ticket'),
      }),
    );
  };

  fetchTicketFiles = (id: TicketId) => {
    return SupportTicketAPI.fetchTicketFiles(id).pipe(
      this.ticketRepo.ops.updateOne(files => ({
        id,
        changes: () => ({ files }),
      })),
    );
  };

  updateTicketAction = (id: TicketId, action: SupportTicketActionName, actionId: string, compact = false, closeAllTasks?: boolean) => {
    return SupportTicketAPI.updateTicketAction(id, action, actionId, compact, closeAllTasks).pipe(
      this.ticketRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_ticket'),
      }),
    );
  };

  updateTicketOrg = (id: TicketId, orgId: OrgId, data: TicketOrgRequest) => {
    return SupportTicketAPI.updateTicketOrg(id, orgId, data).pipe(
      this.ticketRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_ticket'),
      }),
    );
  };

  bulkAction = (ids: TicketId[], status: SupportTicketStatus, closeAllTasks?: boolean) => {
    return SupportTicketAPI.bulkAction(ids, status, closeAllTasks).pipe(
      handleMessage({
        type: requestMessage('update_tickets'),
      }),
    );
  };
}
